/**
 * useCrossSellProducts — fetches cross-sell products via CIO client SDK.
 *
 * CIO Path A §2: cross-sell is client-only. First paint renders nothing;
 * after hydration the CIO SDK call fires, then maps the response directly
 * (no SCAPI hop) to the CrossSellProduct shape used by CrossSellSection.
 *
 * Source: sfcc-website/org_hec/cartridge/templates/default/product/components/recomendation.isml
 */
import { useEffect, useState } from "react";

import { buildProductUrl } from "~/hec/utils/product-slug";
import { fetchCioRecommendations } from "~/utils/cio-recommendations.client";
import { useCioClient } from "~/utils/constructor";

import type { CrossSellProduct } from "../components/cross-sell-section";

// TODO: confirm correct CIO pod ID for cross-sell list with stakeholders
const CROSS_SELL_POD_ID = "pdp-2";
const MAX_CROSS_SELL = 5;

/**
 * Normalize CIO recs `data.brand` for GA4 `item_brand` (SFCC-11011). The recs
 * feed stores brand as a stringified object — e.g.
 * `"{'icon': '', 'name': 'Caring Mill™'}"` — not a plain string. Extract the
 * `name`; fall back to a plain-string brand; drop anything else so GA4 never
 * receives the raw object-string. (Limitation: brand names containing a literal
 * `'` would truncate at the apostrophe — acceptable for current feed data.)
 */
const normalizeCioBrand = (raw: unknown): string | undefined => {
  if (typeof raw !== "string" || !raw) return undefined;
  const nameMatch = raw.match(/'name'\s*:\s*'([^']*)'/);
  if (nameMatch) return nameMatch[1] || undefined;
  return raw.startsWith("{") ? undefined : raw;
};

export function useCrossSellProducts(product: Record<string, unknown>) {
  const productId = product.id as string | undefined;

  const [products, setProducts] = useState<CrossSellProduct[]>([]);
  // Default true so consumers can render a skeleton on first paint instead
  // of a brief empty gap before the CIO client mounts and the fetch fires.
  const [isLoading, setIsLoading] = useState(true);
  // CIO `result_id` + per-product `strategy.id` flow into the
  // `recommendation_click` beacon for attribution. Captured here so the
  // <CrossSellSection> tile click handlers can fire the SDK call.
  const [resultId, setResultId] = useState<string | undefined>(undefined);
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const cio = useCioClient();

  useEffect(() => {
    if (!productId) {
      setIsLoading(false);
      return;
    }
    if (!cio) return;
    let cancelled = false;
    setProducts([]);
    setResultId(undefined);
    setStrategyIds([]);
    setIsLoading(true);

    fetchCioRecommendations(cio, CROSS_SELL_POD_ID, {
      numResults: MAX_CROSS_SELL,
      itemIds: [productId],
    }).then(res => {
      if (cancelled) return;
      setIsLoading(false);
      if (!res) return;

      const raw = (res.response.results ?? []).slice(0, MAX_CROSS_SELL);
      const crossSellProducts: CrossSellProduct[] = raw
        .map(item => {
          const data = item.data ?? {};
          const id = data["id"] as string;
          // Match PLP's mapper (plp-shared-utils.server.ts) — prefer
          // `product_name`, fall back to CIO's `value`. Don't fall back to
          // `id` for the slug; an id-as-name slug
          // ("/fsa12345/FSA12345.html") is noise.
          const name = (data["product_name"] as string) || item.value;
          // CIO `url` field uses the variation_id rather than the master and
          // can also drift from SCAPI's slug. Ignore it — slugify the master
          // name locally (Option B in SFCC-11163) so every storefront tile
          // shares one slug source.
          const url = buildProductUrl(id, name);
          return {
            id,
            // Tile-name fallback to id keeps the row visible if CIO drops
            // both `product_name` and `value`; the slug above deliberately
            // does NOT fall back to id (would emit a noisy id-as-slug URL).
            name: name || id,
            price: (data["price"] as number) ?? undefined,
            imageUrl: data["image_url"] as string | undefined,
            url,
            orderable: (data["inventory_orderable"] as boolean) ?? true,
            // GA4 view_item_list (SFCC-11011): brand + variant are the only
            // analytics fields CIO recs `data` carries; category has no source
            // here (see buildGa4ListItems). Path-A-safe — no extra fetch.
            // `data.brand` is a stringified object in the feed — normalize it.
            brand: normalizeCioBrand(data["brand"]),
            variationId: data["variation_id"] as string | undefined,
          };
        })
        .filter(p => Boolean(p.id))
        // Drop unorderable recs — getOrderableRecommendations() parity (SFCC-11749).
        .filter(p => p.orderable !== false);

      // Build strategyIds parallel to crossSellProducts so consumers can
      // index by position when firing the click beacon.
      const strategyById = new Map<string, string>();
      for (const item of raw) {
        const pid = (item.data?.["id"] as string | undefined) ?? "";
        const sid = (item.strategy?.id as string | undefined) ?? "";
        if (pid) strategyById.set(pid, sid);
      }

      setResultId(res.result_id);
      setStrategyIds(crossSellProducts.map(p => strategyById.get(p.id) ?? ""));
      setProducts(crossSellProducts);
    });

    return () => {
      cancelled = true;
    };
  }, [productId, cio]);

  return {
    products,
    isLoading,
    hasRecommendations: isLoading || products.length > 0,
    podId: CROSS_SELL_POD_ID,
    resultId,
    strategyIds,
  };
}
